/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useRouter, useTranslations } from "@i18n/next";
import { HallowABFlag, useAB } from "@packages/ab";
import type { UseSessionProps } from "@packages/sdk";
import {
  appendSearchParamToUrl,
  URL_ONBOARDING,
  useSession,
} from "@packages/sdk";
import type { AuthFlow } from "@packages/ui";
import {
  Auth,
  cssMediaQueryMinWidthTablet,
  HallowLogoPlainText,
  numericPercentages,
  spacing,
  Text,
} from "@packages/ui";
import { DataTestId } from "@tools/playwright/global";
import { useSearchParams } from "next/navigation";
import { useEffect, useMemo, useState } from "react";

const containerStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  flexWrap: "nowrap",
  height: "90vh",
  justifyContent: "center",
  width: numericPercentages[100],
};
const logoStyle: Interpolation = {
  alignSelf: "center",
  height: 24,
  width: 102,
  [cssMediaQueryMinWidthTablet]: { height: 32, width: 141 },
};
const subtitleStyle: Interpolation = {
  alignSelf: "center",
  paddingBottom: spacing.m,
  paddingTop: spacing.m,
};
const infoContainerStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  paddingLeft: spacing.ml,

  paddingRight: spacing.ml,
  textAlign: "center",
};

export const LoginClient = () => {
  const searchParams = useSearchParams();
  const redirect =
    searchParams.get("redirect") ??
    searchParams.get("loginRedirect") ??
    searchParams.get("subscribeRedirect");
  const [authFlow, setAuthFlow] = useState<AuthFlow>(
    searchParams.get("flow") as AuthFlow,
  );
  const checkSubscription = searchParams.get("checkSubscription") === "true";
  const reason = searchParams.get("reason");
  const referralName = searchParams.get("referral_name");
  const campaignName = searchParams.get("campaign_name");
  const router = useRouter();
  const t = useTranslations();
  const { status, jwtPayload } = useSession();
  const [checked, setChecked] = useState<boolean>(false);
  const { flags } = useAB();

  const redirectTarget = useMemo(() => {
    // don't redirect to the login page
    if (!redirect || redirect?.startsWith("/login")) return null;
    try {
      const target = new URL(redirect, window.location.href);
      for (const [name, val] of searchParams.entries()) {
        if (name === "flow") continue;
        if (name === "redirect") continue;
        target.searchParams.append(name, val);
      }
      if (target.hostname === window.location.hostname)
        return `${target.pathname}${target.search}`;
      return target.href;
    } catch (e) {
      return null;
    }
  }, [redirect, searchParams]);

  useEffect(() => {
    if (status !== "loading") setChecked(true);
    if (status === "authenticated" && !checked) {
      handleAuth({ isNewUser: false });
    }
  }, [status]);

  const handleAuth: UseSessionProps["onAuthenticated"] = ({ isNewUser }) => {
    if (checkSubscription && isNewUser && !jwtPayload?.isSubscribed) {
      router.push(
        `/upgrade/subscribe?redirect=${encodeURIComponent(`/home${isNewUser ? "?welcome=true" : ""}`)}`,
      );
      return;
    }
    // we use location.assign here instead of router.push because router.push is too proactive when it comes
    // to pages that are blocked in the middleware (proxy.ts), and the user ends up back at the login screen
    // again even though they're already logged in; this hard redirect (which is not dangerous vis-a-vis app
    // state, because a logged-out user has no meaningful state) solves that problem by re-engaging the
    // middleware with a new set of cookies
    if (isNewUser) {
      location.assign(
        appendSearchParamToUrl({
          key: "welcome",
          url: redirectTarget || "/home",
          value: "true",
        }) || URL_ONBOARDING,
      );
    } else {
      location.assign(redirectTarget || "/home");
    }
  };

  return (
    <div css={containerStyle}>
      {authFlow === "main" && (
        <div css={infoContainerStyle}>
          <HallowLogoPlainText css={logoStyle} />
          <Text
            size="m"
            type="body"
            css={subtitleStyle}
            data-testid={DataTestId.loginMessage}
          >
            {reason === "referral"
              ? !!referralName
                ? t("referral_landing_title", { "0": referralName })
                : t("referral_landing_title_no_name")
              : reason === "campaign"
                ? !!campaignName
                  ? t("campaign_landing_title", { "0": campaignName })
                  : t("campaign_landing_title_no_name")
                : t("choose_log_in_method")}
          </Text>
        </div>
      )}
      <Auth
        onAuth={handleAuth}
        onFlowChange={(newFlow) => setAuthFlow(newFlow)}
        authMainFlowProps={{
          allowStudentLogin:
            flags?.[HallowABFlag.WebKidsProfileLaunch]?.isTargeted ?? false,
        }}
      />
    </div>
  );
};
